import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Signal, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import moment, { Moment } from 'moment';
import { PlatformEphemerisService } from '@data/_generated-api/platform-api/services/platform-ephemeris.service';
import { HdPlanetKey } from '../chart/bodygraph/planet-grid/hd-planet-display-config';
import {
  HdTransitNowNextPlanet,
  HdTransitPlanetSegments,
  HdTransitPosition,
  HdTransitSegment,
  HdTransitStation,
  TransitGranularity,
  getTransitPlanetId,
  getTransitPlanetsParam,
  getTransitSourceKey,
  toHdTransitNowNextPlanet,
  toHdTransitPlanetSegments,
  toHdTransitPosition,
} from './transit.models';
import { HdTransitRetrogradeSpan, clipRetrogradeSpans, mergeRetrogradeSpans } from './transit-retrograde.utils';

/**
 * Data-laag van de transits-feature: haalt segmenten, now-next en posities op
 * via de gegenereerde `PlatformEphemerisService` en publiceert state als
 * readonly signals. Keuzes:
 *
 * - **Promises i.p.v. toSignal**: de calls zijn expliciet parameter-gestuurd
 *   (venster/planeten/granulariteit) en geen continue streams; `firstValueFrom`
 *   op de gegenereerde Observables is het bestaande repo-patroon
 *   (zie `HdChartLibraryService`). Components awaiten de promise en lezen
 *   loading/error via de signals.
 * - **Fouten gooien niet naar componenten**: `getSegments`/`getNowNext` vangen
 *   API-fouten af, zetten het bijbehorende error-signal en geven een lege
 *   array terug — componenten tonen de error-state (besluit 17).
 * - **Segmenten worden nooit geclipt**: de API levert échte overgangsgrenzen
 *   die buiten het venster kunnen reiken; de view clipt.
 *
 * Chiron hoort standaard bij de data — welke planeten worden gevraagd kiest de
 * caller (de UI filtert via de `showChiron`-setting, besluit 9).
 */

/** Cache-entry van één (granulariteit, UTC-dag, planetId)-chunk. */
interface TransitSegmentChunk {
  readonly segments: readonly HdTransitSegment[];
  readonly stations: readonly HdTransitStation[];
  /** De retrograde-perioden, geknipt op deze UTC-dag (aansluitend samengevoegd bij het assembleren). */
  readonly retrogradeSpans: readonly HdTransitRetrogradeSpan[];
}

/** Laatste now-next-response per planetSet-signatuur, met geldig-tot. */
interface TransitNowNextCacheEntry {
  readonly planetKeys: readonly HdPlanetKey[];
  readonly planets: readonly HdTransitNowNextPlanet[];
  /** Vroegste nextLine/nextGate.atUtc — daarna is de entry verlopen. */
  readonly validUntilUtc: Moment;
}

const DAY_KEY_FORMAT = 'YYYY-MM-DD';
const MS_PER_DAY = 86_400_000;
/**
 * API-limiet voor `granularity=line` (spiegelt `MaxLineWindow` uit de
 * backend-EphemerisController): grotere vensters worden client-side in
 * aaneengesloten sub-runs van maximaal dit aantal dagen opgehaald.
 */
const MAX_LINE_GRANULARITY_DAYS = 92;
/**
 * Blokken van de jaren-weergave ({@link TransitDataService.getLongRangeSegments}):
 * precies de API-limiet voor `granularity=gate` (spiegelt `MaxGateWindow` uit de
 * backend: 5 × 365 dagen), op een vaste indeling vanaf de start van de
 * ephemeris-dekking. Elk blok is één call voor alle gevraagde planeten.
 */
const LONG_RANGE_BLOCK_MS = 5 * 365 * MS_PER_DAY;
/** Ephemeris-dekking (1800–2399, max exclusief) — de blokken blijven erbinnen. */
const LONG_RANGE_DOMAIN_MIN_MS = Date.UTC(1800, 0, 1);
const LONG_RANGE_DOMAIN_MAX_MS = Date.UTC(2400, 0, 1);
/** Fallback-TTL als een now-next-response om wat voor reden dan ook géén next-overgang bevat. */
const NOW_NEXT_FALLBACK_TTL_HOURS = 1;
/** Generieke foutmelding als de API geen `error`-tekst meestuurt (i18n-keys volgen in F2). */
const GENERIC_SEGMENTS_ERROR = 'Transit-data momenteel niet beschikbaar';

/** ISO-8601 UTC-string voor de API, repo-idioom (`hd-chart-create.utils.ts`). */
/** Maximaal aantal momenten per batch-opvraging (spiegel van EphemerisController.MaxBatchMoments). */
const POSITIONS_BATCH_MAX = 500;

function formatUtcForApi(time: Moment): string {
  return moment.utc(time).format('YYYY-MM-DDTHH:mm:ss[Z]');
}

function chunkKey(granularity: TransitGranularity, dayKey: string, planetId: number): string {
  return `${granularity}|${dayKey}|${planetId}`;
}

function longRangeBlockKey(blockIndex: number, planetId: number): string {
  return `${blockIndex}|${planetId}`;
}

/** Halfopen venster [fromMs, toMs) van een blok, begrensd op de ephemeris-dekking. */
function longRangeBlockWindow(blockIndex: number): { readonly fromMs: number; readonly toMs: number } {
  const fromMs = LONG_RANGE_DOMAIN_MIN_MS + blockIndex * LONG_RANGE_BLOCK_MS;
  return { fromMs, toMs: Math.min(fromMs + LONG_RANGE_BLOCK_MS, LONG_RANGE_DOMAIN_MAX_MS) };
}

/** Indexen van de blokken die het halfope venster [fromMs, toMs) raken, oplopend. */
function longRangeBlockIndexes(fromMs: number, toMs: number): number[] {
  const first = Math.floor((Math.max(fromMs, LONG_RANGE_DOMAIN_MIN_MS) - LONG_RANGE_DOMAIN_MIN_MS) / LONG_RANGE_BLOCK_MS);
  const last = Math.floor((Math.min(toMs, LONG_RANGE_DOMAIN_MAX_MS) - 1 - LONG_RANGE_DOMAIN_MIN_MS) / LONG_RANGE_BLOCK_MS);
  const indexes: number[] = [];
  for (let index = first; index <= last; index++) indexes.push(index);
  return indexes;
}

/** Alle UTC-dagen ('YYYY-MM-DD') die het halfope venster [fromUtc, toUtc) raakt, oplopend. */
function utcDayKeysInRange(fromUtc: Moment, toUtc: Moment): string[] {
  const days: string[] = [];
  const day = fromUtc.clone().startOf('day');
  while (day.isBefore(toUtc)) {
    days.push(day.format(DAY_KEY_FORMAT));
    day.add(1, 'day');
  }
  return days;
}

/** Groepeert oplopend gesorteerde dag-keys in aaneengesloten runs (opeenvolgende dagen). */
function contiguousDayRuns(sortedDayKeys: readonly string[]): string[][] {
  const runs: string[][] = [];
  for (const dayKey of sortedDayKeys) {
    const last = runs[runs.length - 1];
    const previous = last?.[last.length - 1];
    if (last != null && previous != null && (Date.parse(dayKey) - Date.parse(previous)) / MS_PER_DAY === 1) {
      last.push(dayKey);
    } else {
      runs.push([dayKey]);
    }
  }
  return runs;
}

/**
 * Splitst runs die de API-limiet voor lijn-granulariteit overschrijden in
 * aaneengesloten sub-runs van maximaal `MAX_LINE_GRANULARITY_DAYS` dagen —
 * elke sub-run wordt één call (bv. het ~276-daagse week-basisbereik → 3 calls).
 * Gate-granulariteit (limiet 5 jaar) blijft ongesplitst.
 */
function capRunsToGranularityLimit(runs: readonly string[][], granularity: TransitGranularity): string[][] {
  if (granularity !== 'line') return [...runs];
  const capped: string[][] = [];
  for (const run of runs) {
    for (let start = 0; start < run.length; start += MAX_LINE_GRANULARITY_DAYS) {
      capped.push(run.slice(start, start + MAX_LINE_GRANULARITY_DAYS));
    }
  }
  return capped;
}

/**
 * Suggestion-tekst uit een 400-response van de segments-endpoint die
 * `granularity=gate` aanbeveelt (bv. "gebruik granularity=gate"), anders `null`.
 */
function getGateGranularitySuggestion(error: unknown): string | null {
  const httpError = error as HttpErrorResponse;
  if (httpError?.status !== 400) return null;
  const body = httpError.error as { suggestion?: unknown } | null | undefined;
  const suggestion = typeof body?.suggestion === 'string' ? body.suggestion : null;
  return suggestion != null && /granularity\s*=\s*gate/i.test(suggestion) ? suggestion : null;
}

/** Leesbare fouttekst: de `error`-tekst uit de response-body, anders een generieke melding. */
function getTransitErrorMessage(error: unknown): string {
  const httpError = error as HttpErrorResponse;
  const body = httpError?.error as { error?: unknown } | null | undefined;
  if (typeof body?.error === 'string' && body.error.length > 0) return body.error;
  return GENERIC_SEGMENTS_ERROR;
}

function nonNull<T>(value: T | null): value is T {
  return value != null;
}

/** Vroegste nextLine/nextGate.atUtc over alle planeten; `null` als er geen next-overgangen zijn. */
function earliestNextTransition(planets: readonly HdTransitNowNextPlanet[]): Moment | null {
  let earliest: Moment | null = null;
  for (const planet of planets) {
    for (const transition of [planet.nextLine, planet.nextGate]) {
      if (transition == null) continue;
      if (earliest == null || transition.atUtc.isBefore(earliest)) earliest = transition.atUtc;
    }
  }
  return earliest;
}

@Injectable({ providedIn: 'root' })
export class TransitDataService {
  private readonly ephemerisApi = inject(PlatformEphemerisService);

  private readonly _segmentsLoading = signal(false);
  private readonly _segmentsError = signal<string | null>(null);
  private readonly _nowNextLoading = signal(false);
  private readonly _nowNextError = signal<string | null>(null);
  private readonly _granularityFallbackNotice = signal<string | null>(null);

  readonly segmentsLoading: Signal<boolean> = this._segmentsLoading.asReadonly();
  readonly segmentsError: Signal<string | null> = this._segmentsError.asReadonly();
  readonly nowNextLoading: Signal<boolean> = this._nowNextLoading.asReadonly();
  readonly nowNextError: Signal<string | null> = this._nowNextError.asReadonly();
  /**
   * Melding van de automatische gate-fallback (de `suggestion`-tekst van de API),
   * zodat de UI kan melden dat de lijn-weergave tijdelijk poort-niveau toont.
   * `null` zodra een volgende call zonder fallback slaagt.
   */
  readonly granularityFallbackNotice: Signal<string | null> = this._granularityFallbackNotice.asReadonly();

  /**
   * Chunk-cache per (granulariteit, UTC-dag, planetId) met de segmenten/stations
   * die die dag raken. De data is deterministisch (de backend cachet zelf 24 u
   * sliding) — geen evictie binnen de sessie.
   */
  private readonly segmentChunks = new Map<string, TransitSegmentChunk>();
  /** In-flight fetch-promise per chunk — overlappende calls delen zo dezelfde Promise. */
  private readonly segmentChunkInflight = new Map<string, Promise<void>>();
  /**
   * Cache van de jaren-weergave per (blok van 5 jaar, planetId), poort-granulariteit.
   * Bewust los van de dag-chunks: een bereik van 60 jaar zou daar ±22.000 dagen
   * per planeet worden. Deterministisch en klein — geen evictie binnen de sessie.
   */
  private readonly longRangeBlocks = new Map<string, TransitSegmentChunk>();
  private readonly longRangeInflight = new Map<string, Promise<void>>();

  private readonly nowNextCache = new Map<string, TransitNowNextCacheEntry>();
  private readonly nowNextInflight = new Map<string, Promise<HdTransitNowNextPlanet[]>>();

  /**
   * Segmenten + stations per planeet over [fromUtc, toUtc).
   *
   * Alleen de nog-niet-gecachte UTC-dagen worden opgehaald, in ÉÉN call per
   * aaneengesloten missend bereik voor álle gevraagde planeten. Bij een 400
   * met een `granularity=gate`-suggestion volgt automatisch één retry op
   * gate-niveau en wordt {@link granularityFallbackNotice} gezet.
   */
  async getSegments(
    fromUtc: Moment,
    toUtc: Moment,
    planetKeys: readonly HdPlanetKey[],
    granularity: TransitGranularity,
  ): Promise<HdTransitPlanetSegments[]> {
    if (planetKeys.length === 0) return [];
    const from = moment.utc(fromUtc);
    const to = moment.utc(toUtc);
    if (!to.isAfter(from)) return [];

    this._segmentsLoading.set(true);
    this._segmentsError.set(null);
    this._granularityFallbackNotice.set(null);
    try {
      return await this.loadSegments(from, to, planetKeys, granularity);
    } catch (error) {
      const suggestion = getGateGranularitySuggestion(error);
      if (granularity === 'line' && suggestion != null) {
        // Spec §1: bereik te groot voor lijn-granulariteit → automatisch één
        // retry op poort-niveau + melding voor de UI.
        this._granularityFallbackNotice.set(suggestion);
        try {
          return await this.loadSegments(from, to, planetKeys, 'gate');
        } catch (retryError) {
          this._segmentsError.set(getTransitErrorMessage(retryError));
          return [];
        }
      }
      this._segmentsError.set(getTransitErrorMessage(error));
      return [];
    } finally {
      this._segmentsLoading.set(false);
    }
  }

  /**
   * Poort-segmenten + stations per planeet over een lang bereik (de jaren-weergave
   * van de lanes: tientallen jaren trage planeten). Opgehaald in vaste blokken van
   * 5 jaar (de API-limiet voor poort-granulariteit), één call per ontbrekend blok
   * voor alle gevraagde planeten, met in-flight-dedupe. Het resultaat dekt de
   * geraakte blokken volledig (ook buiten [fromUtc, toUtc)); de view clipt.
   */
  async getLongRangeSegments(
    fromUtc: Moment,
    toUtc: Moment,
    planetKeys: readonly HdPlanetKey[],
  ): Promise<HdTransitPlanetSegments[]> {
    if (planetKeys.length === 0) return [];
    const fromMs = moment.utc(fromUtc).valueOf();
    const toMs = moment.utc(toUtc).valueOf();
    if (toMs <= fromMs) return [];

    this._segmentsLoading.set(true);
    this._segmentsError.set(null);
    this._granularityFallbackNotice.set(null);
    try {
      return await this.loadLongRangeSegments(fromMs, toMs, planetKeys);
    } catch (error) {
      this._segmentsError.set(getTransitErrorMessage(error));
      return [];
    } finally {
      this._segmentsLoading.set(false);
    }
  }

  /**
   * Huidige stand + omliggende overgangen per planeet — de goedkope call voor
   * het Nu & Straks-panel. Live-calls (`atUtc = null`) worden per
   * planetSet-signatuur gecachet tot de vroegste next-overgang; expliciete
   * `atUtc`-calls (tijdmachine) lezen/schrijven de cache niet.
   */
  async getNowNext(atUtc: Moment | null, planetKeys: readonly HdPlanetKey[]): Promise<HdTransitNowNextPlanet[]> {
    return this.loadNowNext(atUtc, planetKeys, false);
  }

  /**
   * Besluit 6: het panel tikt client-side; alleen als `now` voorbij de
   * geldig-tot van een gecachte now-next-response is wordt opnieuw bevraagd.
   * Geeft de verse planeten terug, of `null` als er niets ververst hoefde te
   * worden (of nog niets gecachet was).
   */
  async refreshNowNextIfNeeded(now: Moment): Promise<HdTransitNowNextPlanet[] | null> {
    let refreshed: HdTransitNowNextPlanet[] | null = null;
    for (const entry of [...this.nowNextCache.values()]) {
      if (moment.utc(now).isAfter(entry.validUntilUtc)) {
        refreshed = await this.loadNowNext(null, entry.planetKeys, true);
      }
    }
    return refreshed;
  }

  /**
   * Kern van {@link getNowNext}. `skipLiveCache` = true forceert een refetch
   * van de live-cache (gebruikt door {@link refreshNowNextIfNeeded}, dat zelf
   * al op geldig-tot gecontroleerd heeft — de cache-check daar binnen meet
   * met de echte klok en mag een geforceerde refresh niet blokkeren).
   */
  private async loadNowNext(
    atUtc: Moment | null,
    planetKeys: readonly HdPlanetKey[],
    skipLiveCache: boolean,
  ): Promise<HdTransitNowNextPlanet[]> {
    if (planetKeys.length === 0) return [];
    const signature = planetKeys.map(getTransitSourceKey).sort().join(',');
    if (atUtc == null && !skipLiveCache) {
      const cached = this.nowNextCache.get(signature);
      if (cached != null && moment.utc().isBefore(cached.validUntilUtc)) {
        return [...cached.planets];
      }
    }
    const inflightKey = `${signature}|${atUtc == null ? 'live' : formatUtcForApi(atUtc)}`;
    const inflight = this.nowNextInflight.get(inflightKey);
    if (inflight != null) return inflight;

    const promise = this.fetchNowNext(atUtc, planetKeys, atUtc == null ? signature : null);
    this.nowNextInflight.set(inflightKey, promise);
    try {
      return await promise;
    } finally {
      this.nowNextInflight.delete(inflightKey);
    }
  }

  /**
   * Ruwe posities + activaties op één moment (voorbereiding stap 4/5:
   * wheel-naalden e.d.). Dunne passthrough — geen cache; API-fouten worden
   * (nog) niet naar een signal vertaald maar rejecten de promise, de consumer
   * uit stap 4/5 bepaalt de error-UX.
   */
  async getPositions(atUtc: Moment | null, planetKeys: readonly HdPlanetKey[]): Promise<HdTransitPosition[]> {
    if (planetKeys.length === 0) return [];
    const response = await firstValueFrom(
      this.ephemerisApi.getEphemerisPositions({
        atUtc: atUtc == null ? null : formatUtcForApi(atUtc),
        planets: getTransitPlanetsParam(planetKeys),
      }),
    );
    return (response.planets ?? []).map(toHdTransitPosition).filter(nonNull);
  }

  /**
   * Posities op meerdere momenten (exports van dagboek en momenten) via het batch-endpoint,
   * in stukken van {@link POSITIONS_BATCH_MAX}; per item `getPositions` zou bij een groot
   * dagboek tegen de rate-limit lopen. Het resultaat volgt de volgorde van `atUtc`.
   */
  async getPositionsBatch(atUtc: readonly Moment[], planetKeys: readonly HdPlanetKey[]): Promise<HdTransitPosition[][]> {
    if (atUtc.length === 0) return [];
    if (planetKeys.length === 0) return atUtc.map(() => []);
    const result: HdTransitPosition[][] = [];
    for (let start = 0; start < atUtc.length; start += POSITIONS_BATCH_MAX) {
      const chunk = atUtc.slice(start, start + POSITIONS_BATCH_MAX);
      const response = await firstValueFrom(
        this.ephemerisApi.postEphemerisPositionsBatch({
          body: { atUtc: chunk.map(formatUtcForApi), planets: getTransitPlanetsParam(planetKeys) },
        }),
      );
      const moments = response.moments ?? [];
      chunk.forEach((_, index) => result.push((moments[index]?.planets ?? []).map(toHdTransitPosition).filter(nonNull)));
    }
    return result;
  }

  // ── segments: cache + fetch ────────────────────────────────────────────

  private async loadSegments(
    from: Moment,
    to: Moment,
    planetKeys: readonly HdPlanetKey[],
    granularity: TransitGranularity,
  ): Promise<HdTransitPlanetSegments[]> {
    const days = utcDayKeysInRange(from, to);
    const planetIds = planetKeys.map(getTransitPlanetId);

    // — Synchrone sectie: cache/in-flight lezen én nieuwe fetches registreren
    //   zónder await ertussen, zodat concurrente calls elkaars in-flight
    //   chunks zien en nooit dubbel ophalen (in-flight-dedupe).
    const missingDays = days.filter(dayKey =>
      planetIds.some(
        planetId =>
          !this.segmentChunks.has(chunkKey(granularity, dayKey, planetId)) &&
          !this.segmentChunkInflight.has(chunkKey(granularity, dayKey, planetId)),
      ),
    );
    for (const run of capRunsToGranularityLimit(contiguousDayRuns(missingDays), granularity)) {
      const fetchPromise = this.fetchSegmentChunks(granularity, run, planetKeys, planetIds);
      for (const dayKey of run) {
        for (const planetId of planetIds) {
          this.segmentChunkInflight.set(chunkKey(granularity, dayKey, planetId), fetchPromise);
        }
      }
    }
    const waiting = new Set<Promise<void>>();
    for (const dayKey of days) {
      for (const planetId of planetIds) {
        const inflight = this.segmentChunkInflight.get(chunkKey(granularity, dayKey, planetId));
        if (inflight != null) waiting.add(inflight);
      }
    }

    // — Async sectie: wachten op eigen én vreemde (overlappende) fetches.
    await Promise.all([...waiting]);
    return this.assembleSegments(granularity, days, planetKeys, planetIds);
  }

  /**
   * Haalt één aaneengesloten dag-run op in ÉÉN call voor alle gevraagde
   * planeten en verdeelt de response over de per-dag-chunks. Dagen waarop een
   * planeet geen segmenten/stations heeft worden als lege chunk gecachet,
   * zodat ze niet opnieuw opgehaald worden.
   */
  private async fetchSegmentChunks(
    granularity: TransitGranularity,
    runDays: readonly string[],
    planetKeys: readonly HdPlanetKey[],
    planetIds: readonly number[],
  ): Promise<void> {
    try {
      const runStart = moment.utc(runDays[0], DAY_KEY_FORMAT);
      const runEnd = moment.utc(runDays[runDays.length - 1], DAY_KEY_FORMAT).add(1, 'day');
      const response = await firstValueFrom(
        this.ephemerisApi.getEphemerisSegments({
          fromUtc: formatUtcForApi(runStart),
          toUtc: formatUtcForApi(runEnd),
          planets: getTransitPlanetsParam(planetKeys),
          granularity,
        }),
      );
      const runWindow = { fromMs: runStart.valueOf(), toMs: runEnd.valueOf() };
      const byPlanetId = new Map(
        (response.planets ?? [])
          .map(planet => toHdTransitPlanetSegments(planet, runWindow))
          .filter(nonNull)
          .map(planet => [planet.planetId, planet] as const),
      );
      for (const dayKey of runDays) {
        const dayStart = moment.utc(dayKey, DAY_KEY_FORMAT);
        const dayEnd = dayStart.clone().add(1, 'day');
        for (const planetId of planetIds) {
          const planet = byPlanetId.get(planetId);
          this.segmentChunks.set(chunkKey(granularity, dayKey, planetId), {
            segments: (planet?.segments ?? []).filter(
              segment => segment.fromUtc.isBefore(dayEnd) && segment.toUtc.isAfter(dayStart),
            ),
            stations: (planet?.stations ?? []).filter(
              station => !station.atUtc.isBefore(dayStart) && station.atUtc.isBefore(dayEnd),
            ),
            retrogradeSpans: clipRetrogradeSpans(planet?.retrogradeSpans ?? [], dayStart.valueOf(), dayEnd.valueOf()),
          });
        }
      }
    } finally {
      for (const dayKey of runDays) {
        for (const planetId of planetIds) {
          this.segmentChunkInflight.delete(chunkKey(granularity, dayKey, planetId));
        }
      }
    }
  }

  /**
   * Bouwt het resultaat uit de dag-chunks: segmenten die meerdere dagen raken
   * zitten in meerdere chunks en worden gededupliceerd op hun echte starttijd
   * (segmenten sluiten aan, dus fromUtc is uniek per planeet).
   */
  private assembleSegments(
    granularity: TransitGranularity,
    days: readonly string[],
    planetKeys: readonly HdPlanetKey[],
    planetIds: readonly number[],
  ): HdTransitPlanetSegments[] {
    return planetKeys.map((planetKey, index) => {
      const planetId = planetIds[index];
      const segmentsByStart = new Map<number, HdTransitSegment>();
      const stationsByStart = new Map<number, HdTransitStation>();
      const retrogradeSpans: HdTransitRetrogradeSpan[] = [];
      for (const dayKey of days) {
        const chunk = this.segmentChunks.get(chunkKey(granularity, dayKey, planetId));
        if (chunk == null) continue;
        for (const segment of chunk.segments) segmentsByStart.set(segment.fromUtc.valueOf(), segment);
        for (const station of chunk.stations) stationsByStart.set(station.atUtc.valueOf(), station);
        retrogradeSpans.push(...chunk.retrogradeSpans);
      }
      return {
        planetId,
        sourceKey: getTransitSourceKey(planetKey),
        planetKey,
        segments: [...segmentsByStart.values()].sort((a, b) => a.fromUtc.valueOf() - b.fromUtc.valueOf()),
        stations: [...stationsByStart.values()].sort((a, b) => a.atUtc.valueOf() - b.atUtc.valueOf()),
        // Per dag geknipt: aaneensluitende stukken over middernacht weer samenvoegen.
        retrogradeSpans: mergeRetrogradeSpans(retrogradeSpans),
      };
    });
  }

  // ── lange bereiken (jaren-weergave): blok-cache + fetch ────────────────

  private async loadLongRangeSegments(
    fromMs: number,
    toMs: number,
    planetKeys: readonly HdPlanetKey[],
  ): Promise<HdTransitPlanetSegments[]> {
    const blockIndexes = longRangeBlockIndexes(fromMs, toMs);
    const planetIds = planetKeys.map(getTransitPlanetId);

    // Synchroon registreren (geen await ertussen), zoals bij de dag-chunks.
    for (const blockIndex of blockIndexes) {
      const missing = planetIds.some(
        planetId =>
          !this.longRangeBlocks.has(longRangeBlockKey(blockIndex, planetId)) &&
          !this.longRangeInflight.has(longRangeBlockKey(blockIndex, planetId)),
      );
      if (!missing) continue;
      const fetchPromise = this.fetchLongRangeBlock(blockIndex, planetKeys, planetIds);
      for (const planetId of planetIds) this.longRangeInflight.set(longRangeBlockKey(blockIndex, planetId), fetchPromise);
    }
    const waiting = new Set<Promise<void>>();
    for (const blockIndex of blockIndexes) {
      for (const planetId of planetIds) {
        const inflight = this.longRangeInflight.get(longRangeBlockKey(blockIndex, planetId));
        if (inflight != null) waiting.add(inflight);
      }
    }

    await Promise.all([...waiting]);
    return planetKeys.map((planetKey, index) => {
      const planetId = planetIds[index];
      const segmentsByStart = new Map<number, HdTransitSegment>();
      const stationsByStart = new Map<number, HdTransitStation>();
      const retrogradeSpans: HdTransitRetrogradeSpan[] = [];
      for (const blockIndex of blockIndexes) {
        const block = this.longRangeBlocks.get(longRangeBlockKey(blockIndex, planetId));
        if (block == null) continue;
        // Een segment over een blokgrens zit in beide blokken: ontdubbelen op zijn échte start.
        for (const segment of block.segments) segmentsByStart.set(segment.fromUtc.valueOf(), segment);
        for (const station of block.stations) stationsByStart.set(station.atUtc.valueOf(), station);
        retrogradeSpans.push(...block.retrogradeSpans);
      }
      return {
        planetId,
        sourceKey: getTransitSourceKey(planetKey),
        planetKey,
        segments: [...segmentsByStart.values()].sort((a, b) => a.fromUtc.valueOf() - b.fromUtc.valueOf()),
        stations: [...stationsByStart.values()].sort((a, b) => a.atUtc.valueOf() - b.atUtc.valueOf()),
        retrogradeSpans: mergeRetrogradeSpans(retrogradeSpans),
      };
    });
  }

  private async fetchLongRangeBlock(
    blockIndex: number,
    planetKeys: readonly HdPlanetKey[],
    planetIds: readonly number[],
  ): Promise<void> {
    try {
      const blockWindow = longRangeBlockWindow(blockIndex);
      const response = await firstValueFrom(
        this.ephemerisApi.getEphemerisSegments({
          fromUtc: formatUtcForApi(moment.utc(blockWindow.fromMs)),
          toUtc: formatUtcForApi(moment.utc(blockWindow.toMs)),
          planets: getTransitPlanetsParam(planetKeys),
          granularity: 'gate',
        }),
      );
      const byPlanetId = new Map(
        (response.planets ?? [])
          .map(planet => toHdTransitPlanetSegments(planet, blockWindow))
          .filter(nonNull)
          .map(planet => [planet.planetId, planet] as const),
      );
      for (const planetId of planetIds) {
        const planet = byPlanetId.get(planetId);
        this.longRangeBlocks.set(longRangeBlockKey(blockIndex, planetId), {
          segments: planet?.segments ?? [],
          stations: (planet?.stations ?? []).filter(
            station => station.atUtc.valueOf() >= blockWindow.fromMs && station.atUtc.valueOf() < blockWindow.toMs,
          ),
          retrogradeSpans: planet?.retrogradeSpans ?? [],
        });
      }
    } finally {
      for (const planetId of planetIds) this.longRangeInflight.delete(longRangeBlockKey(blockIndex, planetId));
    }
  }

  // ── now-next ───────────────────────────────────────────────────────────

  private async fetchNowNext(
    atUtc: Moment | null,
    planetKeys: readonly HdPlanetKey[],
    cacheSignature: string | null,
  ): Promise<HdTransitNowNextPlanet[]> {
    this._nowNextLoading.set(true);
    this._nowNextError.set(null);
    try {
      const response = await firstValueFrom(
        this.ephemerisApi.getEphemerisNowNext({
          atUtc: atUtc == null ? null : formatUtcForApi(atUtc),
          planets: getTransitPlanetsParam(planetKeys),
        }),
      );
      const planets = (response.planets ?? []).map(toHdTransitNowNextPlanet).filter(nonNull);
      if (cacheSignature != null) {
        this.nowNextCache.set(cacheSignature, {
          planetKeys,
          planets,
          validUntilUtc: earliestNextTransition(planets) ?? moment.utc().add(NOW_NEXT_FALLBACK_TTL_HOURS, 'hour'),
        });
      }
      return planets;
    } catch (error) {
      this._nowNextError.set(getTransitErrorMessage(error));
      return [];
    } finally {
      this._nowNextLoading.set(false);
    }
  }
}
